import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SvgLoaderComponent } from '../../images/svg-loader/svg-loader.component';

@Component({
  selector: 'app-base-select',
  standalone: true,
  imports: [CommonModule, FormsModule, SvgLoaderComponent],
  templateUrl: './base-select.component.html',
  styleUrl: './base-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseSelectComponent),
      multi: true
    }
  ]
})
export class BaseSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() id?: string;
  @Input() name?: string;
  @Input() placeholder = '';
  @Input() value: any = null;
  @Input() options: any[] = [];
  @Input() displayField?: string; // Optional: only used for objects
  @Input() isFluid = false;
  @Input() disabled = false;
  @Input() mt = '';
  @Input() mb = '';

  @Output() focusEvent = new EventEmitter<FocusEvent>();
  @Output() blurEvent = new EventEmitter<FocusEvent>();
  @Output() valueChange = new EventEmitter<any>();

  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    // Re-sync value when options change (e.g., async loading in edit mode)
    if (changes['options'] && this.options && this.value) {
      this.value = this.findMatchingOption(this.value);
      this.onChange(this.value);
      this.valueChange.emit(this.value);
    }
  }

  onSelect(value: any) {
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  handleFocus(event: FocusEvent) {
    this.focusEvent.emit(event);
  }

  handleBlur(event: FocusEvent) {
    this.onTouched();
    this.blurEvent.emit(event);
  }

  // Render option as string by default, or use displayField for objects
  optionDisplay(option: any): string {
    if (this.displayField && option && typeof option === 'object') {
      return option[this.displayField] || String(option);
    }
    return String(option);
  }

  // Find matching option in the options array
  private findMatchingOption(value: any): any {
    if (!value || !this.options) return null;
    // For strings, match directly
    if (typeof value === 'string') {
      return this.options.find(opt => opt === value) || null;
    }
    // For objects, match by displayField or strict equality
    return this.options.find(opt => opt === value || (this.displayField && opt && value && opt[this.displayField] === value[this.displayField])) || null;
  }

  writeValue(obj: any): void {
    this.value = this.findMatchingOption(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}